/**
 * Client Service
 * ==============
 * Handles client management operations for providers.
 */

import { supabase } from '../lib/supabase'
import { emailService } from './emailService'
import type {
  Client,
  ClientInsert,
  ClientUpdate,
  ClientStatus,
  Assignment,
  Program,
} from '../types/database.types'

// Type for assignment with program details (for client portal)
export interface ClientPortalAssignment extends Assignment {
  program?: Pick<Program, 'id' | 'title' | 'description'>
  progress_summary?: {
    total_items: number
    completed_items: number
  }
}

// Type for client portal data (client + their assignments)
export interface ClientPortalData {
  client: Client
  assignments: ClientPortalAssignment[]
}

export interface ClientWithStats extends Client {
  assignment_count?: number
  active_assignment_count?: number
}

export interface ClientFilters {
  status?: ClientStatus
  search?: string
  hasAccount?: boolean  // Filter by clients who have created an account (user_id exists)
}

export const clientService = {
  /**
   * Get all clients for the current provider
   */
  async getClients(
    providerId: string,
    filters?: ClientFilters
  ): Promise<ClientWithStats[]> {
    let query = supabase
      .from('clients')
      .select('*')
      .eq('provider_id', providerId)
      .order('created_at', { ascending: false })

    if (filters?.status) {
      query = query.eq('status', filters.status)
    }

    if (filters?.search) {
      query = query.or(
        `full_name.ilike.%${filters.search}%,email.ilike.%${filters.search}%`
      )
    }

    // Filter by account status (user_id exists or not)
    if (filters?.hasAccount === true) {
      query = query.not('user_id', 'is', null)
    } else if (filters?.hasAccount === false) {
      query = query.is('user_id', null)
    }

    const { data, error } = await query

    if (error) {
      console.error('Failed to fetch clients:', error)
      throw error
    }

    return data || []
  },

  /**
   * Get a single client by ID
   */
  async getClientById(clientId: string): Promise<Client | null> {
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .eq('id', clientId)
      .single()

    if (error) {
      if (error.code === 'PGRST116') return null
      console.error('Failed to fetch client:', error)
      throw error
    }

    return data
  },

  /**
   * Create a new client
   */
  async createClient(client: ClientInsert): Promise<Client> {
    const { data, error } = await supabase
      .from('clients')
      .insert(client)
      .select()
      .single()

    if (error) {
      console.error('Failed to create client:', error)
      throw error
    }

    return data
  },

  /**
   * Update an existing client
   */
  async updateClient(
    clientId: string,
    updates: ClientUpdate
  ): Promise<Client | null> {
    const { data, error } = await supabase
      .from('clients')
      .update(updates)
      .eq('id', clientId)
      .select()
      .single()

    if (error) {
      if (error.code === 'PGRST116') return null
      console.error('Failed to update client:', error)
      throw error
    }

    return data
  },

  /**
   * Delete a client
   */
  async deleteClient(clientId: string): Promise<boolean> {
    const { error } = await supabase
      .from('clients')
      .delete()
      .eq('id', clientId)

    if (error) {
      console.error('Failed to delete client:', error)
      throw error
    }

    return true
  },

  /**
   * Activate a client (change status from invited to active)
   */
  async activateClient(clientId: string): Promise<Client | null> {
    return this.updateClient(clientId, { status: 'active' })
  },

  /**
   * Archive a client
   */
  async archiveClient(clientId: string): Promise<Client | null> {
    return this.updateClient(clientId, { status: 'archived' })
  },

  /**
   * Generate a new access token for a client
   */
  async regenerateAccessToken(clientId: string): Promise<Client | null> {
    // The database will auto-generate a new UUID
    const { data, error } = await supabase
      .from('clients')
      .update({ access_token: crypto.randomUUID() })
      .eq('id', clientId)
      .select()
      .single()

    if (error) {
      console.error('Failed to regenerate access token:', error)
      throw error
    }

    return data
  },

  /**
   * Set access expiration for a client
   */
  async setAccessExpiration(
    clientId: string,
    expiresAt: Date | null
  ): Promise<Client | null> {
    return this.updateClient(clientId, {
      access_expires_at: expiresAt?.toISOString() || null,
    })
  },

  /**
   * Validate a client access token (for client portal)
   */
  async validateClientToken(
    token: string
  ): Promise<{ client: Client; isValid: boolean } | null> {
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .eq('access_token', token)
      .single()

    if (error || !data) {
      return null
    }

    const isValid =
      data.status === 'active' &&
      (!data.access_expires_at ||
        new Date(data.access_expires_at) > new Date())

    return { client: data, isValid }
  },

  /**
   * Validate a client access token and fetch their assignments (for client portal)
   * This is the main method for the client portal to load all data in one call
   */
  async validateClientAccessToken(
    accessToken: string
  ): Promise<ClientPortalData | null> {
    // First, validate the client token
    const { data: client, error: clientError } = await supabase
      .from('clients')
      .select('*')
      .eq('access_token', accessToken)
      .single()

    if (clientError || !client) {
      console.log('Client access token validation failed:', clientError?.message)
      return null
    }

    // Check if client is in valid status
    if (client.status === 'archived' || client.status === 'expired') {
      console.log('Client status invalid:', client.status)
      return null
    }

    // Check if access has expired
    if (client.access_expires_at && new Date(client.access_expires_at) < new Date()) {
      console.log('Client access token expired')
      return null
    }

    // Update status from 'invited' to 'active' when client views their portal
    // This indicates the client has actually clicked the magic link
    let updatedClient = client
    if (client.status === 'invited') {
      const { data: updated, error: updateError } = await supabase
        .from('clients')
        .update({ status: 'active' })
        .eq('id', client.id)
        .select()

      if (updateError) {
        console.warn('Failed to update client status to active:', updateError)
        // Continue with original client data - non-fatal error
      } else if (updated && updated.length > 0) {
        updatedClient = updated[0]
      }
    }

    // Fetch all active and completed assignments for this client
    const { data: assignments, error: assignmentsError } = await supabase
      .from('assignments')
      .select(`
        *,
        program:programs (id, title, description)
      `)
      .eq('client_id', client.id)
      .in('status', ['active', 'completed'])
      .order('assigned_at', { ascending: false })

    if (assignmentsError) {
      console.error('Failed to fetch client assignments:', assignmentsError)
      // Return client even if assignments fail - they may just have no programs yet
      return { client: updatedClient, assignments: [] }
    }

    return {
      client: updatedClient,
      assignments: (assignments || []) as ClientPortalAssignment[],
    }
  },

  /**
   * Get client access link
   * Uses VITE_APP_URL env variable for production, falls back to window.location.origin for dev
   */
  getClientAccessLink(accessToken: string): string {
    const baseUrl = import.meta.env.VITE_APP_URL || window.location.origin
    return `${baseUrl}/c/${accessToken}`
  },

  /**
   * Send access link email to client
   */
  async sendAccessLinkEmail(
    client: Client,
    providerName: string
  ): Promise<{ success: boolean; error?: string }> {
    if (!client.email) {
      return { success: false, error: 'Client has no email address' }
    }

    if (!client.access_token) {
      return { success: false, error: 'Client has no access token' }
    }

    const accessLink = this.getClientAccessLink(client.access_token)
    const expiresAt = client.access_expires_at
      ? new Date(client.access_expires_at).toLocaleDateString()
      : undefined

    const result = await emailService.sendClientAccessLink(client.email, {
      clientName: client.full_name || 'there',
      providerName,
      accessLink,
      expiresAt,
    })

    return result
  },

  /**
   * Get client count for a provider
   */
  async getClientCount(providerId: string): Promise<number> {
    const { count, error } = await supabase
      .from('clients')
      .select('*', { count: 'exact', head: true })
      .eq('provider_id', providerId)
      .neq('status', 'archived')

    if (error) {
      console.error('Failed to get client count:', error)
      return 0
    }

    return count || 0
  },
}
